import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {
  Box,
  Button,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { bandHeading, partitionLabel } from '../../../utils/incentiveChallengeApi';
import { formatChallengeValue } from '../../../utils/challengeFormat';
import PrizeWonChip from './PrizeWonChip';

/**
 * The payout sheet for a challenge judged round by round: people down the side,
 * rounds across the top, and in a cell the prize that person won that round.
 *
 * Reading across a row is one person's run; reading down a column is one day.
 * A round pays one prize — the highest rung reached in it — so a cell holds at
 * most one. Every played round shows its figure — green with the prize when
 * won, red when not; a dash is a round the person was not in the role for, a
 * dot one not started yet. Only what was won is shown (decided with the user
 * 2026-10-06); the amount behind a won cell is on hover.
 */
const NAME_WIDTH = 200;

const roundHeading = (round) => {
  const from = dayjs(round.partition_start);
  if (round.partition_start === round.partition_end) return from.format('DD MMM');
  const to = dayjs(round.partition_end);
  return from.month() === to.month()
    ? `${from.format('DD')}–${to.format('DD MMM')}`
    : `${from.format('DD MMM')}–${to.format('DD MMM')}`;
};

function RoundCell({ round, type }) {
  // A round still to come, or one the person was not in the role for, has no
  // figure to show — a faint mark keeps the grid reading as a grid, and a dot
  // for a future day so it never looks like a missed one.
  if (round.state === 'upcoming' || round.state === 'absent') {
    return (
      <TableCell align="center" sx={{ p: 0.5 }}>
        <PrizeWonChip empty={round.state === 'upcoming' ? '·' : '—'} />
      </TableCell>
    );
  }

  // Every played round shows what was achieved in it: green with the prize when
  // a rung was cleared, red when it was not (decided with the user 2026-10-07).
  const won = round.tier_reached > 0;
  return (
    <TableCell align="center" sx={{ p: 0.5, whiteSpace: 'nowrap' }}>
      {won && <PrizeWonChip prize={round.prize} />}
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mt: won ? 0.25 : 0,
          fontWeight: 600,
          color: won ? 'success.dark' : 'error.main',
        }}
      >
        {formatChallengeValue(round.achieved, type)}
        {round.state === 'live' ? ' so far' : ''}
      </Typography>
    </TableCell>
  );
}

RoundCell.propTypes = {
  round: PropTypes.object.isRequired,
  type: PropTypes.string,
};

export default function RoundBoard({ analytics, challenge }) {
  const bands = useMemo(() => analytics?.bands || [], [analytics]);
  const [band, setBand] = useState('all');
  const [exporting, setExporting] = useState(false);
  const type = analytics?.challenge?.challenge_type || challenge?.type;
  const block = analytics?.summary?.partition;
  const rounds = block?.rounds || [];

  /** Ranked by rounds won, then prizes, then what they collected. */
  const people = useMemo(() => {
    const list = (analytics?.participants || []).filter(
      (p) => band === 'all' || p.band?.incentive_challenge_band_id === band,
    );
    return [...list].sort(
      (a, b) =>
        (b.rounds_won || 0) - (a.rounds_won || 0) || Number(b.achieved) - Number(a.achieved),
    );
  }, [analytics, band]);

  /** Per round, how many of the people in view won it. */
  const winnersByRound = useMemo(() => {
    const out = new Map();
    for (const p of people) {
      for (const r of p.partition?.rounds || []) {
        if (r.tier_reached > 0) out.set(r.partition_start, (out.get(r.partition_start) || 0) + 1);
      }
    }
    return out;
  }, [people]);

  /**
   * The sheet Finance pays from. Built on demand: exceljs is only needed by
   * this button, so it stays out of the page's bundle until someone clicks.
   */
  const exportSheet = async () => {
    setExporting(true);
    try {
      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Rounds');
      const header = [
        'Name',
        'Role',
        'Target group',
        ...rounds.map(roundHeading),
        'Rounds won',
        'Prizes won',
        'Total achieved',
      ];
      sheet.addRow(header).font = { bold: true };

      for (const p of people) {
        const byStart = new Map((p.partition?.rounds || []).map((r) => [r.partition_start, r]));
        const cells = rounds.map((r) => {
          const mine = byStart.get(r.partition_start);
          // Only what was won, as on screen: anything else is left blank.
          return mine && mine.state !== 'upcoming' && mine.tier_reached > 0 ? mine.prize : '';
        });
        // One prize per round won, in round order — this is the payout list.
        const allPrizes = (p.partition?.rounds || [])
          .filter((r) => r.tier_reached > 0 && r.state !== 'upcoming')
          .map((r) => r.prize);
        sheet.addRow([
          p.user_name,
          p.role_label,
          p.band ? bandHeading(p.band) : p.excluded_reason || '—',
          ...cells,
          p.rounds_won || 0,
          allPrizes.join(', '),
          Number(p.achieved),
        ]);
      }
      sheet.getColumn(1).width = 28;
      sheet.getColumn(header.length - 1).width = 50;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(challenge?.name || 'challenge').replace(/[^\w-]+/g, '_')}_rounds.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  if (!rounds.length) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Nobody has been placed in this challenge yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          mb: 1.5,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          {bands.length > 1 && (
            <TextField
              select
              size="small"
              label="Target group"
              value={band}
              onChange={(e) => setBand(e.target.value)}
              sx={{ minWidth: 240 }}
            >
              <MenuItem value="all">All target groups</MenuItem>
              {bands.map((b) => (
                <MenuItem key={b.incentive_challenge_band_id} value={b.incentive_challenge_band_id}>
                  {bandHeading(b)}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Typography variant="body2" color="text.secondary">
            {partitionLabel(block.type, block.round_days, block.total_rounds)} ·{' '}
            {block.elapsed_rounds} played · {block.rounds_won} rounds won
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          onClick={exportSheet}
          disabled={exporting || people.length === 0}
          startIcon={<Icon icon="mdi:microsoft-excel" width={18} />}
        >
          {exporting ? 'Preparing…' : 'Export'}
        </Button>
      </Box>

      <TableContainer sx={{ maxHeight: 520 }}>
        <Table size="small" stickyHeader sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{ position: 'sticky', left: 0, zIndex: 3, minWidth: NAME_WIDTH, bgcolor: 'grey.100' }}
              >
                Name
              </TableCell>
              {rounds.map((r) => (
                <TableCell
                  key={r.partition_start}
                  align="center"
                  sx={{
                    bgcolor: r.state === 'live' ? 'warning.lighter' : 'grey.100',
                    fontSize: 12,
                    lineHeight: 1.2,
                    minWidth: 96,
                  }}
                >
                  {roundHeading(r)}
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                    {r.state === 'upcoming' ? '—' : `${winnersByRound.get(r.partition_start) || 0} won`}
                  </Typography>
                </TableCell>
              ))}
              <TableCell align="right" sx={{ bgcolor: 'grey.100' }}>
                Rounds won
              </TableCell>
              <TableCell align="right" sx={{ bgcolor: 'grey.100' }}>
                Total achieved
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {people.map((p) => {
              const byStart = new Map((p.partition?.rounds || []).map((r) => [r.partition_start, r]));
              return (
                <TableRow key={p.user_id} hover>
                  <TableCell
                    sx={{ position: 'sticky', left: 0, zIndex: 1, bgcolor: 'background.paper', fontWeight: 500 }}
                  >
                    {p.user_name}
                    <Typography component="span" variant="caption" sx={{ ml: 0.75, color: 'text.secondary' }}>
                      {p.role_label}
                    </Typography>
                    {p.excluded_reason && (
                      <Typography variant="caption" sx={{ display: 'block', color: 'warning.dark' }}>
                        No ladder — {p.excluded_reason}
                      </Typography>
                    )}
                  </TableCell>
                  {rounds.map((r) => (
                    <RoundCell
                      key={r.partition_start}
                      round={byStart.get(r.partition_start) || { ...r, state: 'absent' }}
                      type={type}
                    />
                  ))}
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {p.rounds_won || 0}
                  </TableCell>
                  <TableCell align="right">{formatChallengeValue(p.achieved, type)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

RoundBoard.propTypes = {
  analytics: PropTypes.object,
  challenge: PropTypes.object,
};
